import {
  MDF_ATTACHMENT_MAX_BYTES,
  MDF_ATTACHMENT_MIME_ALLOWLIST,
} from "@colab/shared";
import {
  FileAttachmentField,
  type FileAttachmentRow,
} from "@/components/shared/file-attachment-field";
import { useUploadMdfAttachment } from "./use-upload-mdf-attachment";
import { useRemoveMdfAttachment } from "./use-remove-mdf-attachment";
import type { MdfAttachment } from "./types";

// Sub-bucket 4 — refactored to consume the hoisted FileAttachmentField from
// `components/shared/`. The MDF-specific upload + remove hooks stay (they
// own query-key invalidation against `mdf.draft(requestId)`); the visual
// state machine moves to the shared component.

const MAX_ATTACHMENTS = 10;

type Props = {
  requestId: string;
  attachments: MdfAttachment[];
  disabled?: boolean;
};

export function MdfAttachmentField({ requestId, attachments, disabled }: Props) {
  const upload = useUploadMdfAttachment(requestId);
  const remove = useRemoveMdfAttachment(requestId);

  const rows: FileAttachmentRow[] = attachments.map((a) => ({
    id: a.id,
    file_name: a.file_name,
    size_bytes: a.size_bytes,
    content_type: a.content_type,
  }));

  return (
    <FileAttachmentField
      label="Supporting documents"
      helpText={`Attach up to ${MAX_ATTACHMENTS} files (PDF, image, Word, Excel — max 25 MB each).`}
      attachments={rows}
      multiple
      maxFiles={MAX_ATTACHMENTS}
      maxSizeBytes={MDF_ATTACHMENT_MAX_BYTES}
      acceptedMimeTypes={MDF_ATTACHMENT_MIME_ALLOWLIST}
      disabled={disabled}
      onUpload={async (file) => {
        const result = await upload.mutateAsync(file);
        return {
          id: result.id,
          file_name: file.name,
          size_bytes: file.size,
          content_type: file.type || "application/octet-stream",
        };
      }}
      onRemove={async (id) => {
        await remove.mutateAsync({ id });
      }}
    />
  );
}
